import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Alert, Button, Spinner, Stack } from "@leylion/design-system";
import { request } from "../api/client";

const TenantCurrencyContext = createContext<string | null>(null);

export function useTenantCurrency(): string {
  const currency = useContext(TenantCurrencyContext);
  if (!currency) throw new Error("Tenant currency has not been loaded.");
  return currency;
}

export function TenantCurrencyBoundary({ language, children }: {
  language: "ar" | "en"; children: (currency: string) => ReactNode;
}) {
  const [currency, setCurrency] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    // This existing ContextSelected read returns Tenant.Currency directly.
    // Runtime localization settings may carry a different branch currency.
    request<{ currency: string; currencyConfirmed: boolean }>("/api/v1/onboarding/first-login")
      .then(state => {
        const code = typeof state.currency === "string" ? state.currency.trim().toUpperCase() : "";
        if (!state.currencyConfirmed || !/^[A-Z]{3}$/.test(code ?? "") || code === "XXX")
          throw new Error("Organization currency is not confirmed.");
        if (!cancelled) setCurrency(code);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [attempt]);
  if (failed) return <Stack><Alert tone="danger" title={language === "ar" ? "تعذر تحميل عملة المؤسسة" : "Organization currency unavailable"}>
    {language === "ar" ? "تأكد من اعتماد عملة المؤسسة ثم أعد المحاولة." : "Confirm the organization currency, then retry."}
  </Alert><Button onClick={() => setAttempt(value => value + 1)}>{language === "ar" ? "إعادة المحاولة" : "Retry"}</Button></Stack>;
  if (!currency) return <Spinner label={language === "ar" ? "تحميل العملة" : "Loading currency"} />;
  return <TenantCurrencyContext.Provider value={currency}>{children(currency)}</TenantCurrencyContext.Provider>;
}
